/**
 * ============================================================================
 * FourBall_HandicapManager
 * ============================================================================
 *
 * Purpose
 * -------
 * Encapsulates all Four Ball handicap logic.  It determines where strokes are
 * received and answers questions about strokes throughout the round.
 *
 * This object hides whether handicaps are based on:
 *
 *   • Individual player handicaps
 *   • Team handicaps
 *
 * Callers should never need to know which handicap mode is being used.
 *
 *
 * Initialization
 * --------------
 * Construct the object once after Four Ball teams and handicap information
 * have been loaded from the scorecard.
 *
 * Typical initialization:
 *
 *   1. Load Four Ball GameData from the scorecard.
 *   2. Create or load the Four Ball teams.
 *   3. Create the FourBall_HandicapManager.
 *   4. Initialize it using:
 *        • Handicap mode
 *        • Team information
 *        • Stored player handicaps
 *        • Stored team handicaps (if applicable)
 *        • Course hole handicap rankings
 *
 * Once initialized, the object is read-only for the remainder of the round.
 *
 *
 * Public Responsibilities
 * -----------------------
 * • Determine whether a player receives a stroke on a hole.
 *
 * • Determine whether a team receives a stroke on a hole.
 *
 * • Determine which holes are stroke holes.
 *
 * • Return the number of strokes received by a player on a hole.
 *
 * • Return the number of strokes received by a team on a hole.
 *
 * • Return the CSS stroke pattern used to color scorecard cells.
 *
 * • Hide all handicap calculations from the rest of the application.
 *
 *
 * Design Notes
 * ------------
 * This class knows everything about handicaps.
 *
 * It does NOT calculate match scores.
 *
 * It does NOT determine hole winners.
 *
 * It does NOT update the UI.
 *
 * Those responsibilities belong to other Four Ball components.
 * ============================================================================
 *
 * ============================================================================
 * Public Interface
 * ============================================================================
 *
 * initialize(...)
 *   Initializes the handicap manager from the scorecard's Four Ball GameData,
 *   the stored player handicaps, team information and course handicap
 *   rankings. This method must be called once before any other methods.
 *
 * isPlayerStrokeHole(holeIndex, playerId)
 *   Returns true if the specified player receives a handicap stroke on the
 *   specified hole.
 *
 * isTeamStrokeHole(holeIndex, teamId)
 *   Returns true if the specified team receives a handicap stroke on the
 *   specified hole.
 *
 * getPlayerStrokes(holeIndex, playerId)
 *   Returns the number of handicap strokes (normally 0 or 1) received by the
 *   specified player on the specified hole.
 *
 * getTeamStrokes(holeIndex, teamId)
 *   Returns the number of handicap strokes (normally 0 or 1) received by the
 *   specified team on the specified hole.
 *
 * getStrokePattern(holeIndex)
 *   Returns the CSS stroke pattern identifier for the specified hole.
 *
 *   This is used by the scorecard to color the score cells for handicap
 *   strokes. The caller does not need to know how the pattern is determined.
 *
 * getStrokeHoles(...)
 *   Returns the collection of holes on which the specified player or team
 *   receives handicap strokes.
 *
 *
 * ============================================================================
 * Design Contract
 * ============================================================================
 *
 * Callers ask questions about handicap strokes.
 *
 * Callers do NOT inspect handicap mode, percentages or calculations.
 *
 * This object is responsible for hiding whether handicap strokes are based on:
 *
 *     • Individual player handicaps
 *     • Team handicaps
 *
 * The rest of the application should never contain logic such as:
 *
 *     if (handicapMode == ...)
 *
 * All handicap-related decisions should be made by this object.
 *
 * ============================================================================
 */
/**
 * Owns Four Ball handicap setup and stroke allocation.
 *
 * This object answers stroke questions only. It does not score holes,
 * determine winners, update the DOM, or save to the database.
 */
export class FourBall_HandicapManager {
    constructor(args) {
        this.teams = args.teams;
        this.handicapSetup = args.handicapSetup;
        this.playersById = new Map(args.players.map(p => [p.memberId, p]));
        this.holesInRound = args.holesInRound;
        this.holeHandicaps = args.holeHandicaps;
        this.playerStrokesByHole = this.buildEmptyPlayerStrokeMap();
        this.teamStrokesByHole = this.buildEmptyTeamStrokeMap();
        this.allocateStrokes();
    }
    getMode() {
        return this.handicapSetup.mode;
    }
    getPlayerStrokesOnHole(playerId, holeIndex) {
        return this.playerStrokesByHole[playerId]?.[holeIndex] ?? 0;
    }
    getTeamStrokesOnHole(teamId, holeIndex) {
        return this.teamStrokesByHole[teamId]?.[holeIndex] ?? 0;
    }
    playerGetsStrokeOnHole(playerId, holeIndex) {
        return this.getPlayerStrokesOnHole(playerId, holeIndex) > 0;
    }
    teamGetsStrokeOnHole(teamId, holeIndex) {
        return this.getTeamStrokesOnHole(teamId, holeIndex) > 0;
    }
    holeHasAnyStroke(holeIndex) {
        return (this.getTeamStrokesOnHole("team1", holeIndex) > 0 ||
            this.getTeamStrokesOnHole("team2", holeIndex) > 0 ||
            this.teams.team1.some(playerId => this.getPlayerStrokesOnHole(playerId, holeIndex) > 0) ||
            this.teams.team2.some(playerId => this.getPlayerStrokesOnHole(playerId, holeIndex) > 0));
    }
    getPlayerStrokeHoles(playerId) {
        return this.getStrokeHoles(this.playerStrokesByHole[playerId] ?? []);
    }
    getTeamStrokeHoles(teamId) {
        return this.getStrokeHoles(this.teamStrokesByHole[teamId] ?? []);
    }
    /**
     * Returns a score-cell stroke pattern for a hole. */
    getStrokePatternClassForHole(holeIndex) {
        if (this.handicapSetup.mode === "teamHandicaps") {
            const team1Stroke = this.getTeamStrokesOnHole("team1", holeIndex) > 0 ? 1 : 0;
            const team2Stroke = this.getTeamStrokesOnHole("team2", holeIndex) > 0 ? 1 : 0;
            if (team1Stroke === 0 && team2Stroke === 0)
                return "sp_0";
            return `sp_${team1Stroke}${team2Stroke}`;
        }
        if (this.handicapSetup.mode === "playerHandicaps") {
            const p1 = this.getPlayerStrokesOnHole(this.teams.team1[0], holeIndex) > 0 ? 1 : 0;
            const p2 = this.getPlayerStrokesOnHole(this.teams.team1[1], holeIndex) > 0 ? 1 : 0;
            const p3 = this.getPlayerStrokesOnHole(this.teams.team2[0], holeIndex) > 0 ? 1 : 0;
            const p4 = this.getPlayerStrokesOnHole(this.teams.team2[1], holeIndex) > 0 ? 1 : 0;
            if (p1 + p2 + p3 + p4 === 0)
                return "sp_0";
            return `sp_${p1}${p2}${p3}${p4}`;
        }
        return "sp_0";
    }
    allocateStrokes() {
        if (this.handicapSetup.mode === "teamHandicaps") {
            this.allocateTeamHandicapStrokes();
            return;
        }
        this.allocatePlayerHandicapStrokes();
    }
    allocateTeamHandicapStrokes() {
        const team1Handicap = this.handicapSetup.team1Handicap ?? 0;
        const team2Handicap = this.handicapSetup.team2Handicap ?? 0;
        const diff = Math.min(36, Math.abs(team1Handicap - team2Handicap));
        if (diff <= 0)
            return;
        const receivingTeam = team1Handicap > team2Handicap ? "team1" : "team2";
        this.teamStrokesByHole[receivingTeam] = FourBall_HandicapManager.allocateStrokesPerHole(diff, this.holesInRound, this.holeHandicaps);
    }
    allocatePlayerHandicapStrokes() {
        const playerIds = [...this.teams.team1, ...this.teams.team2];
        const percent = this.handicapSetup.playerHandicapPercent ?? 100;
        const effectiveHandicaps = playerIds.map(playerId => ({
            playerId,
            handicap: this.getEffectivePlayerHandicap(playerId, percent),
        }));
        if (effectiveHandicaps.length === 0)
            return;
        const lowHandicap = Math.min(...effectiveHandicaps.map(p => p.handicap));
        for (const player of effectiveHandicaps) {
            const strokesReceived = Math.min(36, Math.max(0, player.handicap - lowHandicap));
            this.playerStrokesByHole[player.playerId] = FourBall_HandicapManager.allocateStrokesPerHole(strokesReceived, this.holesInRound, this.holeHandicaps);
        }
    }
    getEffectivePlayerHandicap(playerId, percent) {
        const player = this.playersById.get(playerId);
        const handicap = player?.handicap ?? 0;
        return Math.round(handicap * percent / 100);
    }
    buildEmptyPlayerStrokeMap() {
        const map = {};
        for (const playerId of [...this.teams.team1, ...this.teams.team2]) {
            map[playerId] = new Array(this.holesInRound).fill(0);
        }
        return map;
    }
    buildEmptyTeamStrokeMap() {
        return {
            team1: new Array(this.holesInRound).fill(0),
            team2: new Array(this.holesInRound).fill(0),
        };
    }
    getStrokeHoles(strokesPerHole) {
        const holes = [];
        for (let i = 0; i < this.holesInRound; i++) {
            if ((strokesPerHole[i] ?? 0) > 0) {
                holes.push(i);
            }
        }
        return holes;
    }
    static allocateStrokesPerHole(totalStrokes, holes, strokeIndex) {
        const strokesPerHole = new Array(holes).fill(0);
        if (totalStrokes <= 0) {
            return strokesPerHole;
        }
        const si = strokeIndex.slice(0, holes);
        const base = Math.floor(totalStrokes / holes);
        const remainder = totalStrokes % holes;
        for (let h = 0; h < holes; h++) {
            strokesPerHole[h] = base;
        }
        if (remainder > 0) {
            for (let h = 0; h < holes; h++) {
                const siValue = si[h];
                if (typeof siValue === "number" &&
                    siValue >= 1 &&
                    siValue <= remainder) {
                    strokesPerHole[h] += 1;
                }
            }
        }
        return strokesPerHole;
    }
}
//# sourceMappingURL=FourBall_HandicapManager.js.map